import { useCallback, useMemo, useState } from 'react';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
  PUNCTUATION,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { TextNode } from 'lexical';
import { createPortal } from 'react-dom';
import {
  UserSuggestion,
  useUserSuggestions,
} from '../../common/use-user-suggestions';
import { $createMentionNode } from './mention-node';
import { useHistory } from 'react-router';
import { Link } from '../ui';
import { Icon } from '@socialchorus/shared-ui-components';
import DEFAULT_AVATAR_NEW from '../../models/advocate/avatar';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

class MentionTypeAheadOption extends MenuOption {
  id: string;
  name: string;
  avatar: UserSuggestion['avatar'];
  displayAttributes: string[];

  constructor(suggestion: UserSuggestion) {
    super(suggestion.name);
    this.id = String(suggestion.id);
    this.name = suggestion.name;
    this.avatar = suggestion.avatar;
    this.displayAttributes = suggestion.display_attributes;
  }
}

const TRIGGERS = ['@'].join('');
const VALID_CHARS = `[^${TRIGGERS}${PUNCTUATION}\\s]`;
const LENGTH_LIMIT = 75;

// Matches @ followed by valid characters
const AtSignMentionsRegex = new RegExp(
  `(^|\\s|\\()([${TRIGGERS}]((?:${VALID_CHARS}){0,${LENGTH_LIMIT}}))$`
);

function checkForAtSignMentions(
  text: string,
  minMatchLength: number
): MenuTextMatch | null {
  const match = AtSignMentionsRegex.exec(text);
  if (match !== null) {
    const matchingString = match[3];
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + match[1].length,
        matchingString,
        replaceableString: match[2],
      };
    }
  }
  return null;
}

function MentionMenuItem({
  option,
  isSelected,
  onClick,
  onMouseEnter,
}: {
  option: MentionTypeAheadOption;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}) {
  // Inspired by InternalSuggestionPortal.tsx in user-tag-plugin
  const history = useHistory();
  const profileUrl = history.createHref({ pathname: `/profiles/${option.id}` });

  return (
    <div
      className={`lexical-mention-entry${
        isSelected ? ' lexical-mention-entry--active' : ''
      }`}
      role="option"
      aria-selected={isSelected ? true : undefined}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
      onMouseEnter={onMouseEnter}
      ref={(el) => {
        if (isSelected && el) {
          el.scrollIntoView({ block: 'nearest' });
        }
      }}
    >
      <div className="lexical-mention-avatar">
        <img
          src={option.avatar.url || DEFAULT_AVATAR_NEW}
          alt={option.name}
          width={40}
          height={40}
        />
      </div>
      <div className="lexical-mention-entry-text">
        <div className="lexical-mention-name">{option.name}</div>
        {option.displayAttributes.length > 0 && (
          <div className="lexical-mention-details">
            {option.displayAttributes.join(' \u2022 ')}
          </div>
        )}
      </div>
      <Link
        href={profileUrl}
        target="_blank"
        className={'lexical-mention-profile-link'}
      >
        <Icon size={24}>open_in_new</Icon>
      </Link>
    </div>
  );
}

const MentionPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string>('');
  const suggestions = useUserSuggestions(queryString);

  const handleQueryChange = useCallback((matchingString: string | null) => {
    setQueryString(matchingString ?? '');
  }, []);

  const users = useMemo(
    () => suggestions.map((s) => new MentionTypeAheadOption(s)),
    [suggestions]
  );

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeAheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(
          selectedOption.id,
          selectedOption.name,
          'user'
        );
        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode);
        }
        closeMenu();
      });
    },
    [editor]
  );

  const checkForMentionMatch = useCallback((text: string) => {
    return checkForAtSignMentions(text, 1);
  }, []);

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeAheadOption>
      onQueryChange={handleQueryChange}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={users}
      anchorClassName="lexical-mention-anchor"
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) => {
        if (!anchorElementRef.current || users.length === 0) {
          return null;
        }

        return createPortal(
          <div
            className="lexical-mention-dropdown"
            role="listbox"
            aria-label="User suggestions"
          >
            {users.map((user, i) => (
              <MentionMenuItem
                key={user.id}
                option={user}
                isSelected={selectedIndex === i}
                onClick={() => {
                  setHighlightedIndex(i);
                  selectOptionAndCleanUp(user);
                }}
                onMouseEnter={() => setHighlightedIndex(i)}
              />
            ))}
          </div>,
          anchorElementRef.current
        );
      }}
    />
  );
};

export default MentionPlugin;
